import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { PlayerPosition } from '../types'
import FibaBadge from '../components/profile/FibaBadge'
import Avatar from '../components/Avatar'

interface AgentProfileResponse {
  agent: {
    id: string
    name: string
    is_fiba_licensed: boolean
    fiba_license_number: string | null
    bio: string | null
    photo_url: string | null
  }
  profile: {
    slug: string
    headline: string | null
    years_experience: number | null
    notable_placements: string[]
  }
  agency: {
    id: string
    name: string
    country: string | null
    website: string | null
  } | null
  players: Array<{
    id: string
    slug: string
    name: string
    position: PlayerPosition | null
    nationality: string | null
    photo_url: string | null
  }>
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: AgentProfileResponse }

export default function AgentProfile() {
  const { slug } = useParams()
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setState({ kind: 'error', message: 'no_slug' })
      return
    }
    async function load() {
      try {
        const res = await fetch(`/api/agents/${encodeURIComponent(slug!)}`)
        if (cancelled) return
        if (res.status === 404) {
          setState({ kind: 'error', message: 'Agent not found.' })
          return
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `Request failed (${res.status})` })
          return
        }
        const data = (await res.json()) as AgentProfileResponse
        if (cancelled) return
        setState({ kind: 'ready', data })
      } catch (e) {
        if (cancelled) return
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'network_error',
        })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [slug])

  if (state.kind === 'loading') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10 text-hoop-text-3 text-sm">
        Loading…
      </div>
    )
  }
  if (state.kind === 'error') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-hoop-text-2">{state.message}</p>
      </div>
    )
  }

  const { agent, profile, agency, players } = state.data
  return (
    <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
      <Hero agent={agent} profile={profile} agency={agency} />
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {agent.bio && (
            <Card title="About">
              <p className="text-sm text-hoop-text-2 whitespace-pre-line">
                {agent.bio}
              </p>
            </Card>
          )}
          {profile.notable_placements.length > 0 && (
            <Card title="Notable placements">
              <ul className="text-sm text-hoop-text-2 space-y-1 list-disc pl-5">
                {profile.notable_placements.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </Card>
          )}
          <Roster players={players} />
        </div>
        <div className="space-y-6">
          <ContactCard />
        </div>
      </div>
    </div>
  )
}

function Hero({
  agent,
  profile,
  agency,
}: {
  agent: AgentProfileResponse['agent']
  profile: AgentProfileResponse['profile']
  agency: AgentProfileResponse['agency']
}) {
  return (
    <header className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      <div className="flex items-start gap-5">
        <Avatar
          src={agent.photo_url}
          name={agent.name}
          className="w-20 h-20 rounded-full border border-hoop-border flex-shrink-0"
          textClassName="text-xl"
        />
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-hoop-text-3 mb-1">
            Agent
          </div>
          <h1 className="text-3xl font-bold text-hoop-text">
            {agent.name}
          </h1>
          {profile.headline && (
            <p className="text-hoop-text-2 mt-1">{profile.headline}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-hoop-text-2">
            {agency && (
              <span className="px-2 py-0.5 rounded bg-hoop-bg text-hoop-text-2">
                {agency.name}
              </span>
            )}
            <FibaBadge licenseNumber={agent.fiba_license_number} />
            {profile.years_experience != null && (
              <span className="text-hoop-text-3">
                {profile.years_experience} yr{profile.years_experience === 1 ? '' : 's'} experience
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

function Roster({ players }: { players: AgentProfileResponse['players'] }) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-hoop-text uppercase tracking-wider mb-3">
        Roster{' '}
        <span className="text-hoop-text-3 font-normal">
          · {players.length}
        </span>
      </h2>
      {players.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-8 text-center text-sm text-hoop-text-3">
          No public players yet.
        </div>
      ) : (
        <ul className="bg-hoop-surface border border-hoop-border rounded-lg divide-y divide-hoop-border">
          {players.map(p => (
            <li key={p.id} className="flex items-center gap-4 px-4 py-3">
              <Avatar
                src={p.photo_url}
                name={p.name}
                className="w-10 h-10 rounded-md border border-hoop-border flex-shrink-0"
                textClassName="text-xs"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-hoop-text truncate">
                  {p.name}
                </div>
                <div className="text-xs text-hoop-text-3 truncate">
                  {[p.position, p.nationality].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <Link
                to={`/players/${p.slug}`}
                className="text-sm text-hoop-text-2 hover:text-hoop-text"
              >
                View →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// M1 (PII) — no raw agent contact here. Contact happens directly with the
// agent, off HoopRoster for now; "Express interest" on a player records the
// coach's interest (notifies nobody — correct-by-design, 2026-08-07). An
// agent-side Coach Interest inbox is on the roadmap, unscheduled (2026-10-18).
function ContactCard() {
  return (
    <Card title="Contact">
      <p className="text-hoop-text-3 text-xs">
        Contact happens directly with the agent for now. Open one of the players
        below and use “Express interest” to put your interest on record — an
        agent inbox is on the roadmap.
      </p>
    </Card>
  )
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      <h2 className="text-sm font-semibold text-hoop-text uppercase tracking-wider mb-3">
        {title}
      </h2>
      {children}
    </section>
  )
}
